/**
 * Defines an object that serves as a container for properties associated with a group of UIContainer documents.
 * Documents may be groups on any basis.  One common basis for grouping documents is by "type".  A documents type
 * typically refers to the nature of its content.  Examples include a file being edited or a figure presenting graphics.
 * Other groupings are also permitted.  For example Simulink subsystem models might be grouped based on the
 * top-level model they belong to.  A single document may belong to more than one group.  For example Simulink
 * might display both "subsystem" and "scope" type documents which also have particular model associations.
 *
 * These document group properties may be fixed, meaning that they should be set at construction
 * time and not changed or dynamic meaning that they can change to reflect and control the UIContainer's management
 * of the documents belonging to the group.  By extending "Stateful" this class inherits get, set and watch methods
 * that can be used to manage dynamic properties.
 *
 * @module MW/layout/DocumentGroupProperties
 * @copyright Copyright 2016 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "MW/layout/LayoutChildProperty",
    "MW/layout/PerWindowStateful",
    "MW/layout/TabContainer",
    "mw-log/Log"
], function (declare, lang, LayoutChildProperty, PerWindowStateful, TabContainer, Log) {
    /**
     * @class MW/layout/DocumentGroupProperties
     * @classdesc Defines an object to hold properties associated with a group of documents
     */
    var DocumentGroupProperties = declare([PerWindowStateful], {

        /**
         * @property {String}  title                    Title for the group.  For example "Editor" or "Vdp Model"
         * @property {String}  collectiveLabel          Used to refer to the collection of documents in the group.  For example,
         *                                              Close "Editor Files" or Undock "Vdp Subsystems".
         *                                              The collectiveLabel and title properties can be the same.  In fact if
         *                                              only one of these is set its value will be used for both roles.
         * @property {boolean} constrainToSubContainer  If true documents belonging to the group will always appear in a sub-container
         * @property {String}  defaultRegion            Specifies where documents belonging to the group should appear within the document
         *                                              container, absent other information that dictates placement
         * @property {boolean} isDefault                If true, this group's first plus action will be associated with the plus
         *                                              button in empty document container tiles.
         * @property {{callback: function, text: String}}
         *                     plusAction               An "action" to be triggered by the "+" button to create an additional document
         *                                              belonging t the group
         * @property {boolean} retainEmptySubContainer  If true when the last document in a sub-container is closed the empty sub-container
         *                                              will remain.  By default empty sub-containers will be removed.  This property is
         *                                              only supported when the constrainToSubContainer property is true.
         * @property {boolean} showGroupHeader          Specifies whether a popup header should appear when the mouse hovers near the
         *                                              top of a set of tabs belonging to the group.  When shown the header will offer
         *                                              a drop down with options for managing the documents in the group.
         *                                              If this property is not set the header will be shown.
         * @property {boolean}  shrinkTabsToFit         Controls whether the tabs associated with documents belonging to the group
         *                                              can be shrunk below the size required to display the full document title
         *                                              if necessary to forestall the need to scroll tabs
         * @property {String} subContainerTabPosition   Controls the position of document tabs within the sub-container,
         *                                              overriding the default tab position inherited from the outer container.
         *                                              Permissible values are "left", "right", "top" and "bottom.
         * @property {String}  subGroupingFacet         Specifies a document property to be uses for sub-grouping.  Optional
         * @property {Object}  defaultTiling            Specifies the default sub-tiling arrangement for documents within the group
         * @instance
         */
        Fixed: undefined,  // Trick to make the JSDoc generator list the above under "Fixed"

        /**
         * @property {number}   documentCount        Reports the number of open documents belonging to the group (read only)
         * @property {boolean}  isMaximized          Reflects/controls whether documents belonging to the group are maximized.  This
         *                                           property is only meaningful when the group is sub-tiled.
         * @property {boolean}  isOpen               Reflects/controls whether the group is open.  A group is considered open when
         *                                           any documents belonging to it are open or an empty subcontainer associated
         *                                           with the group is open.  The latter will only occur if both constrainToSubContainer
         *                                           and retainEmptySubContainer are true.
         * @property {_Widget}  lastSelected         Reports the most recently selected document in the group (read only)
         * @property {{w: number, h: number}}
         *                      subGridSize          Controls/reflects the sub-tiling of documents belonging to the group
         * @property {number}   tile                 Reflects controls the location of documents belonging to the group within
         *                                           a tiled document container.
         * @instance
         */
        Dynamic: undefined,  // Trick to make the JSDoc generator list the above under "Dynamic"

        /**
         * Creates a DocumentGroupProperties instance
         *
         * @param initialProperties  an object containing properties that will be mixed into the constructed object
         * @instance
         */
        constructor: function (initialProperties) {
            declare.safeMixin(this, initialProperties);

            if (!this.title) {
                this.title = this.collectiveLabel;
            }
            if (!this.collectiveLabel) {
                this.collectiveLabel = this.title;
            }

            Log.assert(this.title, "Neither title nor collectiveLabel specified for document group");

            delete this.Fixed;   // Not a real property just a JSDoc trick
            delete this.Dynamic; // Not a real property just a JSDoc trick
        },

        /**
         * Overrides the inherited method to set the global property as well as the per window property
         *
         * @param window {Window} the window to associate with the value
         * @param key {String} the key used to store/retrieve the value
         * @param value {Object} the value to be stored
         *
         * @instance
         */
        setForWindow: function (window, key, value) {
            this.inherited(arguments);
            this.set(key, value);
        },

        set: function (name, newValue) {
            // Ignore redundant sets
            if (!(typeof name === "string" && this[name] === newValue)) {
                this.inherited(arguments);
            }
        }
    });

    // Constant name definitions for dynamic properties
    DocumentGroupProperties.DOCUMENT_COUNT = "documentCount";
    DocumentGroupProperties.IS_MAXIMIZED = LayoutChildProperty.IS_MAXIMIZED;
    DocumentGroupProperties.IS_OPEN = LayoutChildProperty.IS_OPEN;
    DocumentGroupProperties.LAST_SELECTED = "lastSelected";
    DocumentGroupProperties.SUB_GRID_SIZE = "subGridSize";
    DocumentGroupProperties.TILE = LayoutChildProperty.TILE;

    // Constant name definitions for some fixed properties
    DocumentGroupProperties.SHRINK_TABS_TO_FIT = TabContainer.SHRINK_TABS_TO_FIT;

    return DocumentGroupProperties;
});